import React, { useState } from 'react';
import axios from 'axios';
import { MapPin, Phone, Mail, Clock, Check, Loader2, AlertCircle } from 'lucide-react';
import { useLang, ui } from '../i18n/LanguageContext';
import { contactInfo } from '../mock/data';
import { PageHero } from './About';
import useReveal from '../hooks/useReveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t, lang } = useLang();
  useReveal();
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
      setTimeout(() => setSent(false), 8000);
    } catch (err) {
      setError(
        lang === 'fr'
          ? "Une erreur est survenue. Veuillez réessayer ou nous joindre par téléphone."
          : 'An error occurred. Please try again or contact us by phone.'
      );
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full bg-transparent border-b border-border py-3 text-[#161514] placeholder:text-muted-foreground/70 focus:border-champagne focus:outline-none transition-colors';

  return (
    <div>
      <PageHero
        tag={t(ui.contact.tag)}
        title={t(ui.contact.title)}
        image="https://images.unsplash.com/photo-1533659124865-d6072dc035e1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
      />

      <section className="py-16 md:py-24">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-14">
          {/* Info */}
          <div className="lg:col-span-5 reveal">
            <p className="text-lg text-muted-foreground leading-relaxed mb-10">{t(ui.contact.intro)}</p>
            <div className="space-y-8">
              <div className="flex gap-4">
                <div className="w-11 h-11 shrink-0 rounded-full border border-champagne flex items-center justify-center"><MapPin size={18} className="text-champagne" /></div>
                <div>
                  <p className="eyebrow text-champagne mb-1">{t(ui.contact.address)}</p>
                  <p className="whitespace-pre-line text-[#3a3833]">{contactInfo.address}</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-11 h-11 shrink-0 rounded-full border border-champagne flex items-center justify-center"><Phone size={18} className="text-champagne" /></div>
                <div>
                  <p className="eyebrow text-champagne mb-1">{t(ui.contact.phone)}</p>
                  <a href={`tel:${contactInfo.phone}`} className="text-[#3a3833] hover:text-champagne transition-colors">{contactInfo.phone}</a>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-11 h-11 shrink-0 rounded-full border border-champagne flex items-center justify-center"><Mail size={18} className="text-champagne" /></div>
                <div>
                  <p className="eyebrow text-champagne mb-1">{t(ui.contact.email)}</p>
                  <a href={`mailto:${contactInfo.email}`} className="text-[#3a3833] hover:text-champagne transition-colors">{contactInfo.email}</a>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-11 h-11 shrink-0 rounded-full border border-champagne flex items-center justify-center"><Clock size={18} className="text-champagne" /></div>
                <div>
                  <p className="eyebrow text-champagne mb-1">{t(ui.contact.hours)}</p>
                  <p className="text-[#3a3833]">{t(ui.contact.hoursValue)}</p>
                </div>
              </div>
            </div>
            {contactInfo.isPlaceholder && (
              <p className="text-xs text-muted-foreground/70 mt-8 italic">* {t(ui.footer.placeholderNote)}</p>
            )}
          </div>

          {/* Form */}
          <div className="lg:col-span-7 reveal">
            <div className="bg-secondary p-8 md:p-12">
              {sent && (
                <div className="mb-8 flex items-start gap-3 bg-[#161514] text-white p-5">
                  <Check size={20} className="text-champagne shrink-0 mt-0.5" />
                  <p className="text-sm leading-relaxed">{t(ui.contact.formSuccess)}</p>
                </div>
              )}
              {error && (
                <div className="mb-8 flex items-start gap-3 border border-destructive/40 bg-destructive/5 text-destructive p-5">
                  <AlertCircle size={20} className="shrink-0 mt-0.5" />
                  <p className="text-sm leading-relaxed">{error}</p>
                </div>
              )}
              <form onSubmit={onSubmit} className="space-y-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <input name="name" required value={form.name} onChange={onChange} placeholder={t(ui.contact.formName)} className={inputClass} />
                  <input name="email" type="email" required value={form.email} onChange={onChange} placeholder={t(ui.contact.formEmail)} className={inputClass} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <input name="phone" value={form.phone} onChange={onChange} placeholder={t(ui.contact.formPhone)} className={inputClass} />
                  <input name="subject" value={form.subject} onChange={onChange} placeholder={t(ui.contact.formSubject)} className={inputClass} />
                </div>
                <textarea name="message" required rows={5} value={form.message} onChange={onChange} placeholder={t(ui.contact.formMessage)} className={`${inputClass} resize-none`} />
                <button type="submit" disabled={loading} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#161514] text-white px-10 py-4 text-sm tracking-wide hover:bg-champagne transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed">
                  {loading && <Loader2 size={17} className="animate-spin" />}
                  {t(ui.cta.sendMessage)}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
